import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import {
   Pagination, Modal, Form, InputGroup, 
  } from 'react-bootstrap';
import { Button, ButtonGroup } from '@/shared/components/Button';
import { FaExclamationCircle } from 'react-icons/fa';
import { ShimmerContentBlock } from 'react-shimmer-effects';
import RejectCard from './RejectCard';
import { GlobalContext } from '../../../../../context/GlobalContext';
import ProgressBar from '../../../Loader';
import { SideBarRefreshContext } from '../../../../../context/SideBarRefreshContext';
import { AuthContext } from '../../../../../context/AuthContext';

const Rejected = ({ agentId }) => {
    const { apiGlobal } = useContext(GlobalContext);
    const [bookingData, setBookingData] = useState([]);
    const [filteredBookingData, setFilteredBookingData] = useState({});
    const [tripOption, setTripOption] = useState([]);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [refresh, setRefresh] = useState(false);
    const { sideBarRefresh } = useContext(SideBarRefreshContext);
    const { countryId } = useContext(AuthContext);

        // Search filter states
  const [searchTripName, setSearchTripName] = useState('');
  const [searchStartDate, setSearchStartDate] = useState('');
  const [searchEndDate, setSearchEndDate] = useState('');
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 2;
  
     // Handle page change
     const handlePageChange = (pageNumber) => {
      setCurrentPage(pageNumber);
    };

    useEffect(() => {
      const fetchRejectBookingData = async () => {
        if (!agentId) return; // Prevent unnecessary API calls if agentId is undefined/null

        const formdata = new FormData();
        formdata.append('agent_id', agentId);
        formdata.append('country_id', countryId);
        
        try {
          const response = await fetch(`${apiGlobal}/agent/booking_reject_list.php`, {
            method: 'POST',
            body: formdata,
          });
          const data = await response.json();
          if (data?.status && data?.message) {
            setBookingData(data?.data);
            setFilteredBookingData(data?.data);
            const tripOptions = data?.data?.map(trip => ({
              value: trip?.id,
              label: `${trip?.name } (${trip?.trip_uin})`, 
            }));
            setTripOption(tripOptions);
             setIsLoading(false);
           } else {
            setBookingData({});
            setIsLoading(false);
           }
        } catch (error) {
          console.error(error);
          setIsLoading(true);
        }
      };
    
      fetchRejectBookingData();
    }, [agentId, refresh, sideBarRefresh?.agentBookingRefresh]);

    const filterBookingHandler = (selectedOption) => {
      setSelectedTrip(selectedOption); // Update selected trip
    
      if (!selectedOption) {
        setFilteredBookingData(bookingData); // Reset filter when no trip is selected
        return;
      }
    
      // Filter booking data based on selected trip ID
      const filteredData = bookingData?.filter(trip => trip?.id === selectedOption?.value);
      setFilteredBookingData(filteredData);
    };
    
       // Pagination Logic - Slice data for the current page
       const indexOfLastItem = currentPage * itemsPerPage;
       const indexOfFirstItem = indexOfLastItem - itemsPerPage;
       const currentItems = bookingData.slice(indexOfFirstItem, indexOfLastItem);

     // Filter Data Based on Date Range
     useEffect(() => {
      let filteredData = [...bookingData];
  
      if (searchStartDate) {
        filteredData = filteredData.filter((trip) => {
          const tripStartDate = new Date(trip?.start_date);
          return tripStartDate >= new Date(searchStartDate);
        });
      }
  
      if (searchEndDate) {
        filteredData = filteredData.filter((trip) => {
          const tripEndDate = new Date(trip?.end_date);
          return tripEndDate <= new Date(searchEndDate);
        });
      }
  
      setFilteredBookingData(filteredData);
    }, [searchStartDate, searchEndDate, bookingData]);
  
  // Total pages
  const totalPages = Math.ceil(bookingData.length / itemsPerPage);

   // use Effect to add keyframes for the pulse animation
   useEffect(() => {
    // Add keyframes via JavaScript when component mounts
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes pulse {
        0% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.2); opacity: 0.8; }
        100% { transform: scale(1); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
  }, []);

  if (isLoading) {
    return (
      <div>
        <ProgressBar />
      </div>
    );
  }
    
  return (

    
    <div>
      <div
        className="mb-4"
        style={{
          padding: '15px',
          borderRadius: '8px',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          justifyContent: 'flex-start',
          gap: '10px', // Add gap between the input fields
          alignItems: 'center', // Vertically align input fields and button
          flexWrap: 'wrap', // Make sure the filters stay in a single line
        }}
      >
        <Select
          name="tripName"
          className="react-select w-25 mb-4 mt-3"
          classNamePrefix="react-select"
          options={tripOption}
          isClearable
          value={selectedTrip}
          placeholder="Select a trip"
          onChange={filterBookingHandler}
        />
        <ButtonGroup icons dir="ltr" className="mt-2">
          <Button
            variant="outline-secondary"
            className=""
            title="Refresh"
            size="sm"
            onClick={() => setRefresh(prev => !prev)}
          >
            <span className="lnr lnr-sync text-primary" />
          </Button>
        </ButtonGroup>
        {/* Search Filters */}
 
        <InputGroup className="mb-2" style={{ maxWidth: '160px' }}>
          <Form.Control
            type="date"
            placeholder="Start Date"
            value={searchStartDate}
            onChange={e => setSearchStartDate(e.target.value)}
            style={{
              borderRadius: '25px',
              padding: '8px',
              border: '1px solid #ddd',
            }}
          />
        </InputGroup>
 
        <InputGroup className="mb-2" style={{ maxWidth: '160px' }}>
          <Form.Control
            type="date"
            placeholder="End Date"
            value={searchEndDate}
            onChange={e => setSearchEndDate(e.target.value)}
            style={{
              borderRadius: '25px',
              padding: '8px',
              border: '1px solid #ddd',
            }}
          />
        </InputGroup>
 
        <Button
          variant="outline-danger"
          style={{
            borderRadius: '25px',
            padding: '8px 16px',
            marginLeft: '10px',
            alignSelf: 'center',
          }}
          className="mt-3"
          onClick={() => {
            setSearchTripName('');
            setSearchStartDate('');
            setSearchEndDate('');
          }}
        >
          Clear Filters
        </Button>
        {/* <Button
          variant="outline-primary"
          style={{
            borderRadius: '25px',
            padding: '8px 16px',
            alignSelf: 'center',
          }}
          className="mt-3"
          onClick={() => setShowAddModal(true)}
        >
          Add Trip
        </Button> */}
      </div>
      {/* <Select
        name="tripName"
        className="react-select w-25 mb-4"
        classNamePrefix="react-select"
        options={tripOption}
        isClearable
        value={selectedTrip}
        placeholder="Select a trip"
        onChange={filterBookingHandler}
      /> */}

      <div>
        {filteredBookingData?.length > 0 ? (
               filteredBookingData?.map(booking => (
                 <RejectCard 
                   key={booking?.id}
                   bookingData={booking}
                 />
               ))
             ) : (
               <div style={{
                textAlign: 'center',
                padding: '50px',
                backgroundColor: '#f8d7da',
                borderRadius: '8px',
                marginTop: '20px',
                color: '#721c24',
                fontSize: '16px',
                }}
               >
                 <div
                   style={{
                   fontSize: '48px',
                   marginBottom: '20px',
                   color: '#e63946',
                   animation: 'pulse 1.5s infinite ease-in-out',
                   display: 'inline-block',
                  }}
                 >
                   <FaExclamationCircle style={{ fontSize: '48px', marginBottom: '20px' }} />
                 </div>
                 <p>No Rejected booking available</p>
               </div>
             )}
      </div>
      {/* Pagination Controls */}
      <div style={{
        float: 'right',
        bottom: '20px', 
        right: '20px', 
        zIndex: 999, 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
}}
      >
        <Pagination>
          <Pagination.Prev
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          />
          {Array.from({ length: totalPages }, (_, index) => (
            <Pagination.Item
              key={index + 1}
              active={index + 1 === currentPage}
              onClick={() => handlePageChange(index + 1)}
            >
              {index + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          />
        </Pagination>
      </div>

    </div>
    );
};

Rejected.propTypes = {
  agentId: PropTypes.string.isRequired,

};

export default Rejected;
